import { useTheme } from '@emotion/react';
import type { TooltipContentImplProps } from '@radix-ui/react-tooltip';
import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import { Close } from '@sds-system/assets';
import type { BgColorCode, IconColorCode, TextColorCode, Theme } from '@sds-system/types';
import { getColorValue } from '@sds-system/utils';
import pick from 'lodash.pick';
import { useState } from 'react';
import { Icon } from '..';
import { StyledTooltipContentWrapper, StyledTooltipRoot } from './Tooltip.styled';
import type { TooltipProps } from './Tooltip.types';
import {
  radixTooltipContentPropsKeys,
  radixTooltipPortalPropsKeys,
  radixTooltipRootPropKeys,
} from './Tooltip.types';

export const Tooltip = (props: TooltipProps) => {
  const { children, ...rootProps } = pick(props, radixTooltipRootPropKeys);
  const portalProps = pick(props, radixTooltipPortalPropsKeys);
  const {
    align = 'center',
    alignOffset,
    collisionPadding = 20,
    side = 'top',
    sideOffset = 8,
    onPointerDownOutside,
    arrowPadding = 16,
    ...contentProps
  } = pick(props, radixTooltipContentPropsKeys);
  const { content, styledComponents } = props;
  const theme = useTheme() as Theme;

  const bgColor = styledComponents?.root?.bgColor || (`bg.dimmer.80` as BgColorCode);
  const color =
    styledComponents?.content?.color ||
    (`icon.interactive.inverse` as TextColorCode | IconColorCode);

  const [innerOpen, setInnerOpen] = useState<boolean>(() => {
    if (typeof rootProps.defaultOpen !== 'undefined') return rootProps.defaultOpen;
    if (typeof rootProps.open !== 'undefined') return rootProps.open;
    return false;
  });

  const handleOpenChange = (open: boolean) => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('android') || (userAgent.includes('iphone') && open !== innerOpen))
      return;

    rootProps.onOpenChange?.(open);
    setInnerOpen(open);
  };

  const handlePointerDownOutside: TooltipContentImplProps['onPointerDownOutside'] = (e) => {
    onPointerDownOutside?.(e);
  };

  const toggleOpen = () => setInnerOpen((p) => !p);

  return (
    <Provider>
      <Root {...rootProps} onOpenChange={handleOpenChange} open={innerOpen}>
        <Trigger onClick={toggleOpen} style={{ display: 'flex', flexDirection: 'column' }}>
          {children}
        </Trigger>
        <Portal {...portalProps}>
          <Content
            {...contentProps}
            align={align}
            alignOffset={alignOffset}
            arrowPadding={arrowPadding}
            collisionPadding={collisionPadding}
            onPointerDownOutside={handlePointerDownOutside}
            side={side}
            sideOffset={sideOffset}
          >
            <StyledTooltipRoot
              {...(styledComponents?.root && styledComponents.root)}
              bgColor={bgColor}
            >
              <StyledTooltipContentWrapper
                {...(styledComponents?.content && styledComponents.content)}
                color={color}
              >
                {content}
              </StyledTooltipContentWrapper>
              <Icon
                color={color}
                css={{ cursor: 'pointer' }}
                onClick={toggleOpen}
                size='3XS'
                source={Close}
              />
            </StyledTooltipRoot>
            <Arrow style={{ fill: getColorValue(bgColor, theme) }} />
          </Content>
        </Portal>
      </Root>
    </Provider>
  );
};
